import React, { useCallback, useEffect, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { userErrorMessage } from "../utils/userErrorMessage";
import { Button } from "../components/ui/button";

const statusOptions = [
  { value: "pending", label: "신청" },
  { value: "approved", label: "승인 내역" },
  { value: "rejected", label: "거절 내역" },
];

const paymentLabel = (row) => {
  if (row.payment_status === "paid") return "결제 완료";
  if (row.payment_status === "trial_pending") return "한 달 무료 신청";
  if (row.payment_status === "trial") return "무료체험 중";
  if (row.payment_status === "legacy") return "기존 신청";
  return "결제 대기";
};

async function getRequestError(error) {
  const response = await error?.context?.clone?.().json().catch(() => null);
  return userErrorMessage(response?.error || error);
}

export default function SignupRequests() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const { data, error } = await supabase.functions.invoke("signup-requests", {
        body: { action: "list", status },
      });
      if (error) throw error;
      setRows(data?.requests || []);
    } catch (error) {
      setRows([]);
      setErrorMessage(await getRequestError(error));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleDecision = async (row, action) => {
    const verb = action === "approve" ? "승인" : "거절";
    if (!window.confirm(`${row.user_name}님의 회원 가입 신청을 ${verb}하시겠습니까?`)) return;
    setProcessingId(row.id);
    setErrorMessage("");
    try {
      const { error } = await supabase.functions.invoke("signup-requests", {
        body: { action, id: row.id },
      });
      if (error) throw error;
      await loadRequests();
    } catch (error) {
      setErrorMessage(await getRequestError(error));
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="space-y-1">
      <Header />
      <main className="px-4 pt-2 pb-4 space-y-4">
        <h1 className="text-lg font-semibold">회원 가입 신청</h1>
        <div className="flex flex-wrap items-center gap-4">
          <span className="font-semibold">구분</span>
          <fieldset className="flex flex-wrap gap-2" aria-label="신청 내역 구분">
            {statusOptions.map((option) => (
              <label
                key={option.value}
                className={`cursor-pointer rounded-md border px-4 py-1 text-sm transition focus-within:ring-2 focus-within:ring-blue-500 ${
                  status === option.value
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <input
                  className="sr-only"
                  type="radio"
                  name="signup-request-status"
                  value={option.value}
                  checked={status === option.value}
                  onChange={() => setStatus(option.value)}
                />
                {option.label}
              </label>
            ))}
          </fieldset>
        </div>
        {errorMessage && <p role="alert" className="text-sm text-red-600">{errorMessage}</p>}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border text-sm">
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                {["순번", "신청일", "회사명", "사용자명", "이메일", "결제", "확인"].map((label) => (
                  <th key={label} className="border px-3 py-2 text-center">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td className="border p-4 text-center" colSpan={7}>신청 내역이 없습니다.</td></tr>
              )}
              {loading && <tr><td className="border p-4 text-center" colSpan={7}>조회 중...</td></tr>}
              {!loading && rows.map((row, index) => (
                <tr key={row.id}>
                  <td className="border px-3 py-2 text-center">{index + 1}</td>
                  <td className="border px-3 py-2 text-center whitespace-nowrap">
                    {new Date(row.created_at).toLocaleString("ko-KR", { timeZone: "Asia/Seoul", hour12: false })}
                  </td>
                  <td className="border px-3 py-2">{row.company_name}</td>
                  <td className="border px-3 py-2 text-center">{row.user_name}</td>
                  <td className="border px-3 py-2">{row.email}</td>
                  <td className="border px-3 py-2 text-center whitespace-nowrap">
                    {paymentLabel(row)}
                  </td>
                  <td className="border px-3 py-2 text-center whitespace-nowrap">
                    {status === "pending" ? (
                      <span className="inline-flex gap-2">
                        <Button disabled={!!processingId} onClick={() => handleDecision(row, "approve")}>확인</Button>
                        <Button disabled={!!processingId} onClick={() => handleDecision(row, "reject")}
                          className="bg-red-600 hover:bg-red-700">거절</Button>
                      </span>
                    ) : status === "approved" ? (
                      <span className="inline-flex items-center gap-2">
                        <span>승인됨</span>
                        <Button disabled={!!processingId} onClick={() => handleDecision(row, "reject")}
                          className="bg-red-600 hover:bg-red-700">거절</Button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <span>거절됨</span>
                        <Button disabled={!!processingId} onClick={() => handleDecision(row, "approve")}>승인</Button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
